import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Database } from "lucide-react";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { useFin, PageHeader, Card } from "../FinanceApp";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export const ACCOUNT_TYPES = [
  ["asset", "الأصول"], ["liability", "الالتزامات"], ["equity", "حقوق الملكية"],
  ["revenue", "الإيرادات"], ["cogs", "تكلفة المبيعات"], ["opex", "المصروفات التشغيلية"],
  ["admin_exp", "المصروفات الإدارية"], ["financial_exp", "المصروفات المالية"], ["other", "حسابات أخرى"],
];
const typeLabel = (t) => (ACCOUNT_TYPES.find((x) => x[0] === t) || [t, t])[1];

export default function FinAccounts() {
  const { can } = useFin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await finApi.accounts(true)); } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    try { const r = await finApi.seedAccounts(); toast.success(`تم إنشاء ${r.created} حساباً`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (a) => {
    if (!window.confirm(`حذف الحساب «${a.name}»؟`)) return;
    try { await finApi.deleteAccount(a.id); toast.success("تم الحذف"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="fin-accounts">
      <PageHeader title="دليل الحسابات" subtitle="شجرة الحسابات متعددة المستويات مع الأرصدة"
        action={can("accounts.create") && (
          <div className="flex gap-2">
            {rows.length === 0 && <Button variant="outline" onClick={seed} data-testid="fin-seed-accounts" className="gap-1.5"><Database className="h-4 w-4" /> دليل افتراضي</Button>}
            <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="fin-add-account" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><Plus className="h-4 w-4" /> حساب جديد</Button>
          </div>
        )} />
      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>
          : rows.length === 0 ? <div className="py-16 text-center text-slate-400">لا توجد حسابات — أنشئ الدليل الافتراضي للبدء</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right" data-testid="fin-accounts-table">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr><th className="px-3 py-2 font-semibold">الرمز</th><th className="px-3 py-2 font-semibold">اسم الحساب</th><th className="px-3 py-2 font-semibold">التصنيف</th><th className="px-3 py-2 font-semibold">الطبيعة</th><th className="px-3 py-2 font-semibold">الرصيد</th><th className="px-3 py-2 font-semibold">إجراءات</th></tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} data-testid={`fin-account-${a.code}`} className="border-t border-slate-50 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-mono-nums text-slate-500">{a.code}</td>
                      <td className="px-3 py-2" style={{ paddingRight: `${(a.level - 1) * 14 + 12}px` }}>
                        <span className={a.allow_posting ? "text-slate-800" : "font-bold text-slate-900"}>{a.name}</span>
                        {!a.allow_posting && <span className="mr-2 text-[10px] rounded bg-slate-100 text-slate-500 px-1.5 py-0.5">رئيسي</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{typeLabel(a.type)}</td>
                      <td className="px-3 py-2 text-slate-500">{a.nature === "debit" ? "مدين" : "دائن"}</td>
                      <td className="px-3 py-2 font-mono-nums" dir="ltr">{money(a.balance)} <SaudiRiyal /></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {can("accounts.edit") && <button data-testid={`fin-edit-account-${a.code}`} onClick={() => { setEdit(a); setOpen(true); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                          {can("accounts.delete") && !a.system && <button onClick={() => del(a)} className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
      <AccountDialog open={open} onOpenChange={setOpen} edit={edit} accounts={rows} onSaved={load} />
    </div>
  );
}

function AccountDialog({ open, onOpenChange, edit, accounts, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(edit ? { ...edit, parent_id: edit.parent_id || "__none__" }
      : { code: "", name: "", type: "asset", parent_id: "__none__", allow_posting: true, active: true, currency: "SAR" });
  }, [open, edit]);

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) { toast.warning("الرمز والاسم مطلوبان"); return; }
    setBusy(true);
    try {
      const body = { ...form, parent_id: form.parent_id === "__none__" ? null : form.parent_id };
      if (edit) await finApi.updateAccount(edit.id, body); else await finApi.createAccount(body);
      toast.success("تم الحفظ"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-md">
        <DialogHeader className="text-start"><DialogTitle>{edit ? "تعديل حساب" : "حساب جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>الرمز</Label><Input data-testid="fin-account-code" dir="ltr" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>التصنيف</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="fin-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>اسم الحساب</Label><Input data-testid="fin-account-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>الحساب الأب</Label>
            <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
              <SelectTrigger data-testid="fin-account-parent"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__none__">— بدون (حساب رئيسي) —</SelectItem>
                {accounts.filter((a) => a.id !== edit?.id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
            <div><div className="text-sm font-semibold">قابل للترحيل</div><div className="text-xs text-slate-500">يسمح بتسجيل القيود مباشرة على الحساب</div></div>
            <Switch checked={form.allow_posting} onCheckedChange={(v) => setForm({ ...form, allow_posting: v })} data-testid="fin-account-allow-posting" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
            <div className="text-sm font-semibold">فعّال</div>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button onClick={save} disabled={busy} data-testid="fin-save-account" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
